// Pendo Analytics Integration
// Documentation: https://support.pendo.io/hc/en-us/articles/360031862272-Install-Pendo

import { UserRole } from '../enums';

// Declare pendo on window object
declare global {
  interface Window {
    pendo?: any;
  }
}

const PENDO_API_KEY = import.meta.env.VITE_PENDO_API_KEY || '';

/**
 * Initialize Pendo analytics
 * Only initializes if PENDO_API_KEY is configured
 */
export const initPendo = (): void => {
  // Only initialize if we have the required configuration
  if (!PENDO_API_KEY) {
    console.warn('Pendo not initialized: Missing API key');
    return;
  }

  // Check if Pendo is already initialized
  if (window.pendo) {
    console.warn('Pendo already initialized');
    return;
  }

  try {
    // Pendo installation script
    (function (apiKey) {
      (function (p: any, e: any, n: any, d: any) {
        var v: any, w: any, x: any, y: any, z: any, o: any;
        o = p[d] = p[d] || {};
        o._q = o._q || [];
        v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
        for (w = 0, x = v.length; w < x; ++w)
          (function (m: any) {
            o[m] =
              o[m] ||
              function () {
                o._q[m === v[0] ? 'unshift' : 'push'](
                  [m].concat([].slice.call(arguments, 0))
                );
              };
          })(v[w]);
        y = e.createElement(n);
        y.async = !0;
        y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
        z = e.getElementsByTagName(n)[0];
        z.parentNode.insertBefore(y, z);
      })(window, document, 'script', 'pendo');
    })(PENDO_API_KEY);

    console.log('Pendo initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Pendo:', error);
  }
};

/**
 * Identify user in Pendo with visitor and account information
 * @param user - User data including id, email, name, organization info
 */
export const identifyPendoUser = (user: {
  id: string;
  email?: string;
  name?: string;
  roles?: UserRole[];
  organization?: {
    id: string;
    name: string;
  };
  [key: string]: any;
}): void => {
  if (!window.pendo) {
    return;
  }

  try {
    // Build visitor data
    const visitorData: any = {
      id: user.id,
    };

    if (user.email) visitorData.email = user.email;
    if (user.name) visitorData.full_name = user.name;
    if (user.roles) visitorData.roles = user.roles;

    // Build account data
    const accountData: any = {
      id: user.organization?.id || 'no-org',
    };

    if (user.organization?.name) accountData.name = user.organization.name;

    // Initialize Pendo with visitor and account data
    window.pendo.initialize({
      visitor: visitorData,
      account: accountData,
    });

    console.log('Pendo user identified:', user.id);
  } catch (error) {
    console.error('Failed to identify Pendo user:', error);
  }
};

/**
 * Track custom event in Pendo
 * @param eventName - Name of the event to track
 * @param metadata - Additional metadata for the event
 */
export const trackPendoEvent = (
  eventName: string,
  metadata?: Record<string, any>
): void => {
  if (!window.pendo) {
    return;
  }

  try {
    window.pendo.track(eventName, metadata);
  } catch (error) {
    console.error('Failed to track Pendo event:', error);
  }
};

/**
 * Update Pendo options
 * @param options - Options to update
 */
export const updatePendoOptions = (options: Record<string, any>): void => {
  if (!window.pendo) {
    return;
  }

  try {
    window.pendo.updateOptions(options);
  } catch (error) {
    console.error('Failed to update Pendo options:', error);
  }
};

const pendoUtils = {
  initPendo,
  identifyPendoUser,
  trackPendoEvent,
  updatePendoOptions,
};

export default pendoUtils;
